// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { buttonTransparent } from "@flip/XDL/PreStyle/button";
import { fontConfig } from "@flip/XDL/PreStyle/typography";
import { color } from "@flip/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconProps, IconWrapper } from "@insite/mobius/Icon";
import { MappedLink } from "@insite/mobius/Menu";
import PanelRow, { PanelRowPresentationProps } from "@insite/mobius/PanelMenu/PanelRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import styled, { css, ThemeConsumer, ThemeProps, withTheme } from "styled-components";

export interface PanelMenuPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<PanelMenuPresentationProps>;
        children?: StyledProp<PanelMenuPresentationProps>;
        menu?: StyledProp<PanelMenuPresentationProps>;
    };
    /** Props passed to the typography children of the menu.
     * @themable */
    childTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu close icon.
     * @themable */
    closeIconProps?: IconProps;
    /** Props passed to the menu back icon.
     * @themable */
    backIconProps?: IconProps;
    /** Color of the background of the menu header.
     * @themable */
    headerColor?: string;
    /** Color of the background of the menu body.
     * @themable */
    bodyColor?: string;
    /** Props to be passed into the panel row component.
     * @themable */
    panelRowProps?: PanelRowPresentationProps;
}

export type PanelMenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** The element that triggers the menu. */
        panelTrigger: React.ReactElement;
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /**
         * The nested child structure that will be rendered by the menu.
         * If menu items are provided, children organically passed to the component
         * will not be rendered.
         */
        menuItems?: MappedLink[];
        /** Maximum number of menus that will appear in submenu panels. */
        maxDepth?: number;
        /** Function called to close overlay (Drawer) in which menu resides. Governs presence of `x` icons in headers. */
        closeOverlay?: () => void;
        /** The current URL of the application, used to apply 'current page' styles to the menu item */
        currentUrl?: string;
        /** @ignore internal prop */
        layer: number;
    }
>;

const PanelMenuWrapper = styled.nav<ThemeProps<BaseTheme> & InjectableCss>`
    width: 100%;
    button,
    link {
        &:focus {
            outline-offset: -${getProp("theme.focus.width")};
        }
    }
    > button {
        width: 100%;
    }

    outline: none;
    ${injectCss}
`;

const PanelMenuStyle = styled.div<ThemeProps<BaseTheme> & InjectableCss & { color?: string }>`
    overflow-y: auto;
    height: 100%;
    padding-bottom: 40px;
    background: ${color("common.background")};
    ${injectCss}
`;

const PanelChildren = styled.div<ThemeProps<BaseTheme> & InjectableCss & { displayLayer: number }>`
    max-height: 100vh;
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 100%;
    transition: all ease-in-out ${getProp("theme.transition.duration.short")}ms;
    ${({ displayLayer }) => {
        if (displayLayer <= 0) {
            return css`
                left: 0;
            `;
        }
        return css`
            left: 100%;
        `;
    }}
    ${injectCss}
`;

export const RowWrapper = styled.div<ThemeProps<BaseTheme> & InjectableCss>`
    display: flex;
    align-items: center;
    justify-content: space-between;

    &.last-item {
        margin-bottom: 30px;
    }
    ${injectCss}
`;

/** PanelMenuProps with defaulted properties now listed as not-optional, plus theme. */
export type PanelMenuProps = PanelMenuComponentProps & PanelMenuPresentationProps;

type PanelMenuPropsCompleted = PanelMenuPresentationProps &
    Omit<PanelMenuComponentProps, "maxDepth"> &
    Required<Pick<PanelMenuComponentProps, "maxDepth">>;

class PanelMenu extends React.Component<PanelMenuPropsCompleted> {
    static defaultProps = { maxDepth: 3 };

    state = {
        layer: this.props.layer + 1,
        descriptionId: this.props.descriptionId || uniqueId(),
    };

    UNSAFE_componentWillReceiveProps(nextProps: PanelMenuPropsCompleted) {
        // eslint-disable-line camelcase
        if (nextProps.layer !== this.props.layer + 1 && !nextProps.children && !this.props.children) {
            this.setState({ layer: nextProps.layer + 1 }, () => {});
        }
    }

    moveNextLeft = () => {
        this.setState(({ layer }: { layer: number }) => {
            return { layer: layer - 1 };
        });
    };

    moveNextRight = () => {
        this.setState(({ layer }: { layer: number }) => {
            return { layer: layer + 1 };
        });
    };

    renderMenuItems(childMenuItems: MappedLink[], currentDepth: number, propBuilder: any) {
        const { applyProp } = propBuilder;
        const { currentUrl } = this.props;
        const typographyProps = applyProp("childTypographyProps");
        const panelRowProps = applyProp("panelRowProps");
        const childMenuItemsFiltered = childMenuItems.filter(item => !item.excludeFromNavigation);
        return (
            <>
                {childMenuItemsFiltered.map((item, index) => {
                    const isLastItem = index === childMenuItemsFiltered.length - 1;
                    const renderChildren =
                        item.children?.filter(child => !child.excludeFromNavigation).length &&
                        currentDepth < this.props.maxDepth;
                    const forwardProps: { href?: string } = {};
                    if (item.url) {
                        forwardProps.href = item.url;
                    }

                    let menu;
                    const currentUrlRegex = new RegExp(currentUrl!, "gi");
                    let menuItem: JSX.Element | null = (
                        <PanelRow
                            key={item.title}
                            isCurrent={currentUrl?.match(currentUrlRegex) === item.url?.match(currentUrlRegex)}
                            onClick={this.props.closeOverlay}
                            color={applyProp("bodyColor")}
                            tabIndex={this.state.layer === 0 ? 0 : -1}
                            {...forwardProps}
                            {...panelRowProps}
                        >
                            <Typography
                                data-test-selector={`${this.props["data-test-selector"]}_${item.title}`}
                                {...{
                                    ...typographyProps,
                                    weight: { ...(item as any) }.isFeatured ? 600 : typographyProps?.weight,
                                }}
                            >
                                {item.title}
                            </Typography>
                        </PanelRow>
                    );

                    if (renderChildren) {
                        let panelRow;
                        if (item.url) {
                            panelRow = <PanelRow hasChildren></PanelRow>;
                        } else {
                            panelRow = React.cloneElement(menuItem, { hasChildren: true });
                            menuItem = null;
                        }
                        menu = (
                            <PanelMenu
                                {...this.props}
                                as="div"
                                panelTrigger={panelRow}
                                menuItems={item.children as MappedLink[]}
                                maxDepth={this.props.maxDepth - 1}
                                layer={this.state.layer}
                            />
                        );
                    }
                    return (
                        <RowWrapper key={item.title} className={isLastItem ? "last-item" : ""}>
                            {menuItem}
                            {menu}
                        </RowWrapper>
                    );
                })}
            </>
        );
    }

    // XNG-Changes: stop useless recursion
    renderChildren = (layer: number, menuItems: MappedLink[] | undefined, propBuilder: any) => {
        // if menu closed, don't render the children
        if (layer > 0) {
            return null;
        }

        if (menuItems) {
            return this.renderMenuItems(menuItems, 1, propBuilder);
        }
        return this.props.children;
    };

    render() {
        return (
            <ThemeConsumer>
                {theme => {
                    const {
                        currentUrl,
                        descriptionId,
                        menuItems,
                        panelTrigger,
                        closeOverlay,
                        mergeCss,
                        ...otherProps
                    } = this.props;
                    const propBuilder = applyPropBuilder({ ...this.props, theme }, { component: "panelMenu" });
                    const { translate } = theme;
                    const { layer, descriptionId: navId } = this.state;
                    const { spreadProps, applyProp, applyStyledProp } = propBuilder;
                    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.panelMenu?.defaultProps?.mergeCss;
                    const headerColor = applyProp("headerColor", "common.backgroundContrast");
                    return (
                        <PanelMenuWrapper
                            css={applyStyledProp(["cssOverrides", "wrapper"], resolvedMergeCss)}
                            {...{ "aria-labelledby": navId }}
                            {...otherProps}
                            tabIndex={-1}
                        >
                            {React.cloneElement(panelTrigger, { id: navId, onClick: this.moveNextLeft })}
                            <PanelChildren
                                displayLayer={layer}
                                aria-hidden={layer > 0}
                                css={applyStyledProp(["cssOverrides", "children"], resolvedMergeCss)}
                                data-layer={layer}
                            >
                                <PanelMenuStyle
                                    css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                                    color={applyProp("bodyColor", "common.white")}
                                >
                                    <PanelRow as="div" color="common.white">
                                        <Button
                                            color={headerColor}
                                            onClick={this.moveNextRight}
                                            tabIndex={layer === 0 ? 0 : -1}
                                            css={css`
                                                min-width: 0;
                                                display: flex;
                                                align-items: flex-end;
                                                justify-content: flex-start;
                                                flex: 1;
                                                padding: 0 0 4px 0;
                                                align-items: center;

                                                ${TypographyStyle},
                                                ${IconWrapper} {
                                                    color: ${color("text.main")};
                                                }
                                                ${IconWrapper} {
                                                    margin-right: 8px;
                                                    transform: rotate(90deg);
                                                }
                                                ${buttonTransparent}
                                            `}
                                            aria-label="Menu Back"
                                        >
                                            <ButtonIcon {...spreadProps("backIconProps")} />
                                            <Typography
                                                data-test-selector={`${this.props["data-test-selector"]}_back`}
                                                css={css`
                                                    &&& {
                                                        font-size: 14px;
                                                        line-height: 22px;
                                                        font-weight: ${fontConfig.weight.regular};
                                                    }
                                                `}
                                            >
                                                {translate("Back")}
                                            </Typography>
                                            <VisuallyHidden>{translate("Back")}</VisuallyHidden>
                                        </Button>
                                        {/* {closeOverlay && ( */}
                                        {/*    <Button */}
                                        {/*        color={headerColor} */}
                                        {/*        onClick={closeOverlay} */}
                                        {/*        tabIndex={layer === 0 ? 0 : -1} */}
                                        {/*    > */}
                                        {/*        <ButtonIcon {...spreadProps("closeIconProps")} /> */}
                                        {/*        <VisuallyHidden>{translate("Close")}</VisuallyHidden> */}
                                        {/*    </Button> */}
                                        {/* )} */}
                                    </PanelRow>
                                    {this.renderChildren(layer, menuItems, propBuilder)}
                                </PanelMenuStyle>
                            </PanelChildren>
                        </PanelMenuWrapper>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

/**
 * Menu composed of slide-in panels.
 */

PanelMenu.defaultProps = {
    maxDepth: 3,
};

/** @component */
export default withTheme((props: PanelMenuProps) => <PanelMenu {...(props as PanelMenuPropsCompleted)} />);
